import React, { useEffect, useState } from "react";
import { Loader2, PhoneCall, Users2, Info, CheckCircle2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatDate, formatDateTime } from "@/lib/api";

const ACTIONS = [{ v: "called", l: "Called" }, { v: "met", l: "Met in person" }, { v: "other", l: "Other" }];

function FollowupCard({ f, onLogged }) {
  const [action, setAction] = useState("called");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("ongoing");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (note.trim().length < 2) { toast.error("Please add a short note"); return; }
    setSaving(true);
    try {
      await api.post(`/teacher/student-attendance/followups/${f.id}/log`, { action, note: note.trim(), status });
      toast.success(status === "resolved" ? "Follow-up marked resolved" : "Contact logged");
      setNote("");
      onLogged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const fmt = formatDateTime;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`followup-${f.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-bold text-slate-900 truncate">{f.student_name} <span className="text-slate-400 font-normal">· Roll {f.roll_number}</span></p>
          <p className="text-xs text-slate-500">{f.klass} · Last absent {f.last_absent_date ? formatDate(f.last_absent_date) : "—"}</p>
          {f.guardian_email ? <p className="text-xs text-slate-500 truncate">Parent: {f.guardian_email}</p> : <p className="text-xs text-red-500">No parent email on file</p>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge className="bg-red-600" data-testid={`followup-streak-${f.id}`}>{f.streak} days absent</Badge>
          {f.needs_meeting ? <Badge className="bg-red-900" data-testid={`followup-meeting-${f.id}`}><Users2 className="h-3 w-3 mr-1" />Arrange meeting</Badge>
            : f.needs_call ? <Badge className="bg-amber-500"><PhoneCall className="h-3 w-3 mr-1" />Call parent</Badge> : null}
        </div>
      </div>

      {f.needs_meeting && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">5+ consecutive absences — a meeting request email to the parent has been {f.meeting_email_logged ? "logged (TEST mode)" : "queued"}.</p>}

      {(f.contact_log || []).length > 0 && (
        <div className="space-y-1" data-testid={`followup-log-${f.id}`}>
          {f.contact_log.map((c, i) => (
            <div key={i} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-2">
              <span className="font-semibold capitalize">{c.action}</span> — {c.note} <span className="text-slate-400">· {c.by} · {fmt(c.at)}{c.status === "resolved" ? " · resolved" : ""}</span>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2 pt-1 border-t border-slate-100">
        <div className="flex gap-2 flex-wrap">
          {ACTIONS.map((a) => (
            <Button key={a.v} size="sm" variant={action === a.v ? "default" : "outline"} className={action === a.v ? "bg-blue-900" : ""} onClick={() => setAction(a.v)} data-testid={`followup-action-${a.v}-${f.id}`}>{a.l}</Button>
          ))}
        </div>
        <div>
          <Label className="text-xs">Note (what the parent said / next step)</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Spoke to father — child unwell, back Monday" data-testid={`followup-note-${f.id}`} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button size="sm" variant={status === "ongoing" ? "default" : "outline"} className={status === "ongoing" ? "bg-slate-700" : ""} onClick={() => setStatus("ongoing")} data-testid={`followup-status-ongoing-${f.id}`}>Ongoing</Button>
            <Button size="sm" variant={status === "resolved" ? "default" : "outline"} className={status === "resolved" ? "bg-green-600 hover:bg-green-700" : ""} onClick={() => setStatus("resolved")} data-testid={`followup-status-resolved-${f.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Resolve</Button>
          </div>
          <Button size="sm" onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid={`followup-save-${f.id}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" />Save log</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function StudentFollowups() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/teacher/student-attendance/followups");
      setList(data);
    } catch { toast.error("Could not load follow-ups"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden" data-testid="student-followups">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        Students absent for <b>2+ consecutive days</b> appear here to call the parent. At <b>5+ days</b> a meeting is flagged. Log each contact and mark resolved when done.
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="followups-empty">
          <Info className="h-8 w-8 mx-auto mb-2 text-slate-400" />No active follow-ups. Great attendance!
        </div>
      ) : (
        <div className="space-y-3" data-testid="followups-list">
          {list.map((f) => <FollowupCard key={f.id} f={f} onLogged={load} />)}
        </div>
      )}
    </div>
  );
}
